import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, List, ListItem } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NavLink from './common/NavLink';
import useTeamCompetitionData from './useTeamCompetitionData';
import { slugify } from '../utils/slugify'; 
import { Helmet } from 'react-helmet-async'; // [cite: 394]

const mockTiles = [
  { title: 'Szanse w grupach', description: 'Szanse na konkretne miejsca', link: (id) => `/liga/431/wcPositionMatrix` },
  { title: 'Ranking ELO', description: '...i jego zmiany', link: (id) => `/liga/431/elo-ranking` },  
  { title: 'PODSUMOWANIE', description: 'Podsumowanie fazy pucharowej', link: (id) => `/liga/431/wcSummary` },  
  { title: 'Terminarz', description: 'Lista meczów', link: (id) => `/liga/431/fixtures` },
  { title: 'Trzecie miejsca', description: 'Szanse na awans z 3. miejsca', link: (id) => `/liga/431/WcPointThreshold` },
  { title: 'Własne symulacje', description: 'Stwórz symulację warunkową', link: (id) => `/liga/431/WcYourSimulation` },

];

const GROUPS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

const WcLeaguePage = () => {
  const { id } = useParams();
  const [selectedGroup, setSelectedGroup] = useState('A');

  // Pobranie danych o rozgrywkach (m.in. listy meczów) przy pomocy hooka
  const competitionData = useTeamCompetitionData(id);
  
  // Bezpieczne wyciągnięcie listy meczów (obsługa różnych formatów zwracanych przez hook)
  const matches = useMemo(() => {
    if (!competitionData) return [];
    if (Array.isArray(competitionData)) return competitionData;
    return competitionData.matches || competitionData.data || [];
  }, [competitionData]);

  // Funkcja obliczająca tabelę dla wybranej grupy "w locie"
const groupTable = useMemo(() => {
    if (!matches || matches.length === 0) return [];

    const tableMap = {};

    const initTeamRow = (teamId, teamName) => {
      if (!tableMap[teamId]) {
        tableMap[teamId] = {
          teamId,
          teamName: teamName || `Drużyna ${teamId}`,
          matchesPlayed: 0,
          points: 0,
          goalsScored: 0,
          goalsConceded: 0,
        };
      }
    };

    // Filtrowanie meczów należących do wybranej grupy (np. "A", "Grupa A", "GRUPA A")
    const filteredMatches = matches.filter(m => {
      const matchGroup = (m.group || m.groupName || m.stage || '').toString().toUpperCase();
      return (
        matchGroup === selectedGroup || 
        matchGroup === `GRUPA ${selectedGroup}` || 
        matchGroup.endsWith(` ${selectedGroup}`)
      );
    });

    // 1. Inicjalizacja wszystkich drużyn i kalkulacja tabeli bazowej (całkowitej)
    filteredMatches.forEach(m => {
      const homeId = m.homeTeamId;
      const homeName = m.homeTeamName || (m.homeTeam && m.homeTeam.name);
      const awayId = m.awayTeamId;
      const awayName = m.awayTeamName || (m.awayTeam && m.awayTeam.name);

      if (!homeId || !awayId) return;

      // Inicjuj zawsze, nawet gdy mecz ma status "planned"
      initTeamRow(homeId, homeName);
      initTeamRow(awayId, awayName);

      // Weryfikacja po statusie (ignorowanie wielkości liter dla bezpieczeństwa)
      const isPlayed = (m.status || '').toLowerCase() === 'ended';

      if (isPlayed) {
        const hg = Number(m.homeGoals || 0);
        const ag = Number(m.awayGoals || 0);

        tableMap[homeId].matchesPlayed += 1;
        tableMap[awayId].matchesPlayed += 1;

        tableMap[homeId].goalsScored += hg;
        tableMap[homeId].goalsConceded += ag;
        tableMap[awayId].goalsScored += ag;
        tableMap[awayId].goalsConceded += hg;

        if (hg > ag) {
          tableMap[homeId].points += 3;
        } else if (ag > hg) {
          tableMap[awayId].points += 3;
        } else {
          tableMap[homeId].points += 1;
          tableMap[awayId].points += 1;
        }
      }
    });

    const baseTable = Object.values(tableMap);

    // 2. Grupowanie drużyn po liczbie punktów w celu rozstrzygnięcia remisów punktowych (H2H)
    const teamsByPoints = {};
    baseTable.forEach(team => {
      if (!teamsByPoints[team.points]) {
        teamsByPoints[team.points] = [];
      }
      teamsByPoints[team.points].push(team);
    });

    const finalTable = [];
    
    // Sortowanie unikalnych wartości punktowych od największej do najmniejszej
    const sortedPoints = Object.keys(teamsByPoints).map(Number).sort((a, b) => b - a);

    // 3. Budowanie końcowej tabeli z uwzględnieniem "Małej Tabeli" dla drużyn z tą samą liczbą punktów
    sortedPoints.forEach(pts => {
      const tiedTeams = teamsByPoints[pts];

      if (tiedTeams.length === 1) {
        // Brak remisu punktowego - po prostu dodajemy drużynę do tabeli
        finalTable.push(tiedTeams[0]);
      } else {
        // Mamy 2 lub więcej drużyn z taką samą liczbą punktów -> Budujemy Małą Tabelę
        const tiedTeamIds = tiedTeams.map(t => t.teamId);
        const miniTableMap = {};
        
        tiedTeamIds.forEach(id => {
          miniTableMap[id] = { points: 0, goalsScored: 0, goalsConceded: 0 };
        });

        // Przeliczamy punkty i bramki TYLKO z meczów bezpośrednich między zainteresowanymi
        filteredMatches.forEach(m => {
          const isPlayed = (m.status || '').toLowerCase() === 'ended';
          
          if (isPlayed && tiedTeamIds.includes(m.homeTeamId) && tiedTeamIds.includes(m.awayTeamId)) {
             const hg = Number(m.homeGoals || 0);
             const ag = Number(m.awayGoals || 0);

             miniTableMap[m.homeTeamId].goalsScored += hg;
             miniTableMap[m.homeTeamId].goalsConceded += ag;
             miniTableMap[m.awayTeamId].goalsScored += ag;
             miniTableMap[m.awayTeamId].goalsConceded += hg;

             if (hg > ag) {
               miniTableMap[m.homeTeamId].points += 3;
             } else if (ag > hg) {
               miniTableMap[m.awayTeamId].points += 3;
             } else {
               miniTableMap[m.homeTeamId].points += 1;
               miniTableMap[m.awayTeamId].points += 1;
             }
          }
        });

        // Wewnętrzne sortowanie grupy remisującej
        tiedTeams.sort((a, b) => {
          const miniA = miniTableMap[a.teamId];
          const miniB = miniTableMap[b.teamId];

          // 1. Punkty w małej tabeli (H2H)
          if (miniB.points !== miniA.points) return miniB.points - miniA.points;

          // 2. Różnica bramek w małej tabeli (H2H)
          const miniGdA = miniA.goalsScored - miniA.goalsConceded;
          const miniGdB = miniB.goalsScored - miniB.goalsConceded;
          if (miniGdB !== miniGdA) return miniGdB - miniGdA;

          // 3. Bramki strzelone w małej tabeli (H2H)
          if (miniB.goalsScored !== miniA.goalsScored) return miniB.goalsScored - miniA.goalsScored;

          // --- Jeśli H2H nic nie dało, wracamy do statystyk globalnych ---

          // 4. Ogólna różnica bramek
          const overallGdA = a.goalsScored - a.goalsConceded;
          const overallGdB = b.goalsScored - b.goalsConceded;
          if (overallGdB !== overallGdA) return overallGdB - overallGdA;

          // 5. Ogólne bramki strzelone
          return b.goalsScored - a.goalsScored;
        });

        // Dodajemy posortowaną podgrupę do finalnej tabeli
        finalTable.push(...tiedTeams);
      }
    });

    return finalTable;
  }, [matches, selectedGroup]);
  
  // Przygotowanie nazwy ligi do SEO 
  const fullLeagueName = 'Mistrzostwa Świata';

  return (
    <>
      <Helmet>
		  <title>{`${fullLeagueName} - Symulator i tabela | SymuLiga`}</title>
		  
		  {/* W description dajemy angielską frazę "table simulator" - user widzi to tylko w Google */}
		  <meta 
			name="description" 
			content={`${fullLeagueName} table simulator. Sprawdź szanse na mistrzostwo, awans i spadek. Unikalny kalkulator wyników końcowych oparty o ranking ELO.`} 
		  />
		  <link rel="canonical" href={`https://symuliga.pl/liga/${id}`} />
		  {/* W og:title dla mediów społecznościowych również możemy dodać "simulator" */}
		  <meta property="og:title" content={`${fullLeagueName} symulator - Symulacja tabeli`} />
		  <meta property="og:description" content="Predykcje polskich lig piłkarskich. Sprawdź szanse swojej drużyny!" />
	  </Helmet>

      <Box sx={{ textAlign: 'center', padding: { xs: '5px', md: '10px' } }}>
        {/* H1 - Główny nagłówek dla SEO  */}
        <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 'bold' }}>
          {fullLeagueName}
        </Typography>
        
        {/* H2 - Podtytuł wspierający słowa kluczowe  */}
        <Typography variant="subtitle1" component="h2" sx={{ mb: 3, color: 'text.secondary' }}>
          Symulacja tabeli i szanse drużyn – kalkulator wyników końcowych
        </Typography>

        {/* Kafelki */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {mockTiles.map((tile, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={6} // 3 kolumny na dużych ekranach (12/4 = 3)
            key={tile.title}
          >
            <Link to={typeof tile.link === 'function' ? tile.link(id) : tile.link} style={{ textDecoration: 'none' }}>
              <Card
                sx={{
                  backgroundColor: '#2c2c2c',
                  color: '#ffffff',
                  transition: 'background-color 0.3s',
                  '&:hover': { backgroundColor: '#3a3a3a' },
                  cursor: 'pointer',
                  minHeight: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CardContent
                  sx={{
                    textAlign: 'center',
                    padding: '16px',
                  }}
                >
                  <Typography variant="h6">{tile.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tile.description}
                  </Typography>
                </CardContent>
              </Card>
            </Link>
          </Grid>
        ))}
      </Grid>
	  
	  {/* Sekcja wyboru grupy (Kafelki A-L) 
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', borderBottom: '2px solid #3a3a3a', pb: 1 }}>
          Tabele grupowe fazy zasadniczej
        </Typography>
        
        <Grid container spacing={1} sx={{ mb: 4 }}>
          {GROUPS.map((group) => {
            const isSelected = selectedGroup === group;
            return (
              <Grid item xs={3} sm={2} md={1} key={group}>
                <Card
                  onClick={() => setSelectedGroup(group)}
                  sx={{
                    backgroundColor: isSelected ? '#1976d2' : '#2c2c2c',
                    color: '#ffffff',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: isSelected ? '1px solid #ffffff' : '1px solid transparent',
                    '&:hover': {
                      backgroundColor: isSelected ? '#115293' : '#3a3a3a',
                    },
                  }}
                >
                  <CardContent sx={{ padding: '12px !important' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {group}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Typography variant="h6" sx={{ mb: 2, color: '#ff9800', fontWeight: 'bold' }}>
          Aktualna Sytuacja: Grupa {selectedGroup}
        </Typography>

        {groupTable.length === 0 ? (
          <Paper sx={{ p: 4, backgroundColor: '#2c2c2c', textAlign: 'center' }}>
            <Typography sx={{ color: '#aaa' }}>
              Brak rozegranych meczów lub danych dla grupy {selectedGroup} w tym turnieju.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ backgroundColor: '#2c2c2c', borderRadius: '8px', boxShadow: 3 }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#383838' }}>
                <TableRow>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', width: '60px' }}>Poz</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Drużyna</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', textAlign: 'center', width: '80px' }}>M</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', textAlign: 'center', width: '80px' }}>Pkt</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', textAlign: 'center', width: '120px' }}>Bramki</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupTable.map((teamRow, index) => {
                  // Podświetlenie strefy awansu (miejsca 1 i 2) na zielono
                  const isPromotionZone = index < 2;
                  const rowBgColor = isPromotionZone ? 'rgba(76, 175, 80, 0.1)' : 'transparent';

                  return (
                    <TableRow
                      key={teamRow.teamId}
                      sx={{
                        backgroundColor: rowBgColor,
                        '&:hover': { 
                          backgroundColor: isPromotionZone ? 'rgba(76, 175, 80, 0.18)' : '#363636' 
                        },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold' }}>
                        {index + 1}
                      </TableCell>
                      <TableCell
                        component={Link}
                        to={`/team/${teamRow.teamId}/${slugify(teamRow.teamName)}`}
                        sx={{
                          color: '#ffffff',
                          borderBottom: '1px solid #3a3a3a',
                          textDecoration: 'none',
                          fontWeight: isPromotionZone ? '500' : 'normal',
                          '&:hover': { textDecoration: 'underline', color: '#1976d2' }
                        }}
                      >
                        {teamRow.teamName}
                      </TableCell>
                      <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', textAlign: 'center' }}>
                        {teamRow.matchesPlayed}
                      </TableCell>
                      <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', textAlign: 'center', fontWeight: 'bold' }}>
                        {teamRow.points}
                      </TableCell>
                      <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', textAlign: 'center' }}>
                        {teamRow.goalsScored}:{teamRow.goalsConceded}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
*/}
    </Box>
	</>
  );
};

export default WcLeaguePage;